import React, { useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const text = {
  en: {
    title: "Contact Us",
    subtitle: "Questions, problems or feedback? Send us a message and we'll reply by email.",
    name: "Your name",
    email: "Your email",
    message: "How can we help?",
    send: "Send Message",
    sending: "Sending…",
    sent: "Thanks! Your message has been sent. We'll reply to the email address you gave.",
    another: "Send another message",
    done: "Done",
    close: "Close",
    failed: "Couldn't send your message. Please try again later.",
  },
  zh: {
    title: "联系我们",
    subtitle: "有问题、遇到困难或有建议？给我们留言，我们会通过邮件回复您。",
    name: "您的姓名",
    email: "您的邮箱",
    message: "请描述您的问题",
    send: "发送",
    sending: "发送中…",
    sent: "谢谢！您的留言已发送，我们会回复到您填写的邮箱。",
    another: "再发一条",
    done: "完成",
    close: "关闭",
    failed: "发送失败，请稍后再试。",
  },
};

const inputClass =
  "w-full px-3 py-2 bg-dark-bg border border-dark-border rounded-md focus:outline-none focus:border-primary";

// Public — no login needed. Posts to the backend's /api/contact, which emails
// the message to the support inbox with Reply-To set to the sender.
export default function Contact() {
  const { lang } = useOutletContext();
  const t = text[lang] || text.en;
  const navigate = useNavigate();
  // Back to the user's files if logged in, else the landing page.
  const close = () => navigate(localStorage.getItem("token") ? "/myfiles" : "/");

  // Prefill from the logged-in user, if any.
  const user = (() => {
    try { return JSON.parse(localStorage.getItem("user") || "{}"); } catch { return {}; }
  })();
  const blank = { name: user.name || "", email: user.email || "", message: "", website: "" };

  const [form, setForm] = useState(blank);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || t.failed);
      setSent(true);
      setForm(blank);
    } catch (err) {
      setError(err.message || t.failed);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="w-full max-w-lg mx-auto py-12">
      <h1 className="text-3xl font-bold text-white mb-2 text-center">{t.title}</h1>
      <p className="text-gray-400 text-sm mb-8 text-center">{t.subtitle}</p>

      <div className="relative">
      <button
        type="button"
        onClick={close}
        aria-label={t.close}
        className="absolute top-3 right-4 text-gray-400 hover:text-white text-xl leading-none"
      >
        ✕
      </button>
      {sent ? (
        <div className="bg-dark-card border border-dark-border rounded-xl p-8 text-center space-y-4">
          <p className="text-green-400">{t.sent}</p>
          <button
            type="button"
            onClick={close}
            className="w-full py-3 bg-primary hover:bg-primary-dark text-dark-bg rounded-lg font-semibold transition"
          >
            {t.done}
          </button>
          <button
            type="button"
            onClick={() => setSent(false)}
            className="text-primary hover:underline text-sm"
          >
            {t.another}
          </button>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit}
          className="bg-dark-card border border-dark-border rounded-xl p-8 space-y-4"
        >
          <input
            type="text"
            placeholder={t.name}
            className={inputClass}
            value={form.name}
            onChange={update("name")}
            maxLength={100}
            required
          />
          <input
            type="email"
            placeholder={t.email}
            className={inputClass}
            value={form.email}
            onChange={update("email")}
            maxLength={254}
            required
          />
          <textarea
            placeholder={t.message}
            className={`${inputClass} h-40 resize-y`}
            value={form.message}
            onChange={update("message")}
            maxLength={5000}
            required
          />
          {/* Honeypot: hidden from people, so only bots fill it in. */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="hidden"
            value={form.website}
            onChange={update("website")}
          />

          {error && <p className="text-red-400 text-sm text-center">{error}</p>}

          <button
            type="submit"
            disabled={sending}
            className="w-full py-3 bg-primary hover:bg-primary-dark text-dark-bg rounded-lg font-semibold transition disabled:opacity-60"
          >
            {sending ? t.sending : t.send}
          </button>
        </form>
      )}
      </div>
    </div>
  );
}
